import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { GenericGrid, IGridItem } from "../../../app/common/controls/GenericGrid";
import { useEffect, useMemo } from "react";
import { useStore } from "../../../app/stores/rootStore";

interface Props {
    isChildView?: boolean; // Determines if we use parentCategoryStore
}

export default observer(function CategoryListComponent({ isChildView = false }: Props) {
    const navigate = useNavigate();
    const { categoryStore, parentCategoryStore } = useStore();
    
    const activeStore = isChildView ? categoryStore : parentCategoryStore;

    // Standardized destructuring based on the store being used
    const { 
        items, 
        isLoaded, 
        loadAll,
        setItemload,
        selectedItem
    } = activeStore;

    // Load data only if store indicates it is currently in a "reload" state or empty
    useEffect(() => {
        if (isLoaded) {
            loadAll().then(() => setItemload(false));
        }
    }, [isLoaded, loadAll, setItemload]);

    const handleNavigation = (id: string) => {
        // Trigger a reload state for the target view before navigating
        if (isChildView) {
            categoryStore.setItemload(true);
        } else {
            parentCategoryStore.setItemload(true);
        }
        navigate(`/category/${id}`);
    };

    // Define the predicate based on current state
    const filterByParent = useMemo(() => {
        return (item: IGridItem) => {
            if (isChildView) {
                // If viewing sub-categories, only show items whose parentId matches selected parent
                return item.parentId === selectedItem?.id;
            }
            // If on main view, show only top-level categories (no parentCategory defined)
            // Use (item as any) to access specific fields not in IGridItem if necessary
            const rawItem = item as any;
            return !rawItem.parentId || rawItem.parentId === "";
        };
    }, [isChildView, selectedItem?.id]);

    return (
        <GenericGrid
            title={isChildView ? "Related Categories" : "Curated Luxury & Fashion Essentials"}
            items={items}
            isReloading={isLoaded}
            loadAction={loadAll}
            setReloadAction={setItemload}
            // Logic: Only show items where parentCategory matches current URL ID
            filterPredicate={filterByParent}
            renderItem={(category) => (
                <div className="item features-image" key={category.id}>
                    <div className="item-wrapper">
                        <div className="tag-wrapper">
                            <p className="mbr-tag mbr-fonts-style display-4">
                                <strong>{category.name}</strong>
                            </p>
                        </div>
                        <div className="item-img">
                            {category.displayMode === "video" ? (
                                <video controls autoPlay loop muted>
                                    <source src={category.categoryVideo} type="video/mp4" />
                                </video>
                            ) : (
                                <img src={category.categoryImage} alt={category.title} />
                            )}
                        </div>
                        <div className="card-box">
                            <h4 className="card-title mbr-fonts-style display-7">
                                <strong>{category.title}</strong>
                            </h4>
                            <p className="card-text mbr-fonts-style display-4">
                                {category.description}
                            </p>
                            <div className="mbr-section-btn">
                                <a 
                                    style={{ cursor: 'pointer', padding: '0.8rem 0.6rem' }} 
                                    onClick={() =>{
                                        if (isChildView) {
                                            categoryStore.setItem(category);
                                        } else {
                                            parentCategoryStore.setItem(category);
                                        }
                                        //parentCategoryStore.setItem(category);
                                        handleNavigation(category.id)
                                    }} 
                                    className="btn item-btn btn-black display-4"
                                >
                                    Shop Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        />
    );
});